import { useState, Fragment, useEffect } from "react";

import VisualizationRow from "./VisualizationTable/visualizationRow";
import SearchBar from "../Common/searchBar";
import SideNavigation from "../Common/sideNavigation";

import { API, apiClient } from "../../../Common/lib/api";
import ResponseNotification from "../UserManagement/Modals/responseNotification";

import Pagination from "../Common/pagination";

/**
 * Returns the Visualization Approval page component.
 *
 * @returns Visualization Approval page component.
 */
export default function VisualizationApproval() {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [noSearchResults, setNoSearchResults] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [projectPerPage, setProjectPerPage] = useState(10);

  const [users, setUsers] = useState([]);

  const [notificationShow, setNotificationShow] = useState(false);
  const [notificationPurpose, setNotificationPurpose] = useState("");

  const [requests, setRequests] = useState([]);
  const [projectCount, setProjectCount] = useState(0);
  const [projectPublishStatus, setProjectPublishStatus] = useState({
    PKSK: "",
    newPublishStatus: "",
  });

  const [notification, setNotification] = useState({
    status: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    getVisualizationRequests();
    setProjectCount(0);
  }, [currentPage]);

  /**
   * Gets all of the visualization requests.
   */
  const getVisualizationRequests = async () => {
    setNoSearchResults(false);
    const params = {
      category: "visualizationApproval",
      ordering: "-date",
      offset: currentPage * projectPerPage,
      limit: projectPerPage,
    };

    if (searchTerm.length > 0) {
      params.q = searchTerm;
    }

    try {
      const result = await API.logs.list(params);
      // Only show pending visualization approval requests
      const allItems = result.data.listLogs.items || [];
      const items = allItems.filter(
        (request) =>
          request.status === "pending" ||
          request.status === null ||
          request.status === undefined ||
          request.status === ""
      );
      const total = items.length;

      if (total === 0) {
        setNoSearchResults(true);
      } else {
        setRequests(items);
        setProjectCount(total);
      }

      if (users != []) {
        getUsers();
      }
    } catch (e) {
      // console.log(e);
    }
  };

  /**
   * Retrieves users.
   */
  const getUsers = async () => {
    try {
      const response = await apiClient.get('/admin/users/');
      setPageLoading(false);
      setUsers(response.data.Users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageLoading(false);
      setUsers([]);
    }
  };

  /**
   * Returns the UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date.
   *
   * @param {Integer} unix
   * @returns The UNIX EPOCH timestamp in milliseconds into a formatted MM/dd/YYYY date.
   */
  const formatUNIX = (unix) => {
    return new Date(unix).toLocaleDateString("en-US");
  };

  /**
   * Handles the selected project publishStatus attribute.
   *
   * @param {String} PKSK
   * @param {String} publishStatus
   */
  const projectPublishStatusChange = (PKSK, publishStatus) => {
    setProjectPublishStatus({
      PKSK: PKSK,
      newPublishStatus: publishStatus,
    });
  };

  /**
   * Handles the project search input field.
   *
   * @param {Event Handles} event
   */
  const handleSearch = (event) => {
    setSearchTerm(event.target.value.trim());
  };

  return (
    <>
      <div className="flex flex-col h-fit md:h-screen">
        <div className="h-full bg-gray-50">
          <SideNavigation />

          <main className="lg:pl-72 bg-gray-50 h-full">

            <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
              <ResponseNotification
                as={Fragment}
                show={notificationShow}
                notification={notification}
                setShow={setNotificationShow}
                notificationPurpose={notificationPurpose}
              />

              <div className="py-6 px-3 w-full md:px-5 md:py-12 bg-white border border-gray-200 md:w-full rounded-lg xl:p-12 shadow-sm">
                  <div className="mt-2 flex justify-between w-full">
                    <h1 className="text-2xl py-2 font-bold leading-tight text-gray-900">
                      Visualization Approval
                    </h1>
                    <SearchBar
                      searchPurpose={"doi"}
                      searchTerm={searchTerm}
                      currentPage={currentPage}
                      getProjects={getVisualizationRequests}
                      handleSearch={handleSearch}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>

                <main className="overflow-hidden">
                  {pageLoading ? (
                    <div className="text-base p-40 bg-gray-100 place-content-center">
                      <p className="text-center font-medium">Loading</p>
                    </div>
                  ) : (
                    <>

                      {noSearchResults ? (
                        <div className="text-base p-40 bg-gray-100 place-content-center">
                          <p className="text-center font-medium">No Visualization Requests Found!</p>
                        </div>
                      ) : (
                        <>

                          

                          <table className="w-full">
                            <thead className="bg-gray-100">
                              <tr>
                                <th
                                  scope="col"
                                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:text-base sm:pl-6"
                                >
                                  Project Name
                                </th>

                                <th
                                  scope="col"
                                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                                >
                                  Visualization Title
                                </th>

                                <th
                                  scope="col"
                                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                                >
                                  Created By
                                </th>

                                <th
                                  scope="col"
                                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                                >
                                  Date
                                </th>

                                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                  <span className="sr-only">Action</span>
                                </th>
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 bg-white">
                              {requests.map((project) => (
                                <VisualizationRow
                                  visualizationRequest={project}
                                  formatUNIX={formatUNIX}
                                  projectPublishStatusChange={projectPublishStatusChange}
                                  users={users}
                                  setNotification={setNotification}
                                  setNotificationShow={setNotificationShow}
                                  setNotificationPurpose={setNotificationPurpose}
                                  setRequests={setRequests}
                                />
                              ))}
                            </tbody>
                          </table>

                          <Pagination
                            projectsPerPage={projectPerPage}
                            currentPage={currentPage}
                            projectCount={projectCount}
                            setCurrentPage={setCurrentPage}
                          />
                        </>
                      )}
                    </>
                  )}
                </main>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
